import { useState } from 'react';
import type { Residency } from '@salonomy/types';
import { Button, Card, Pill } from '@salonomy/ui';
import { useExportBook } from '../../data/gdpr.js';
import { useResidencies } from '../../data/practitioner.js';
import { useAuth } from '../../lib/auth.js';
import { useOffline } from '../../lib/offline/provider.js';
import { useToast } from '../../lib/toast.js';
import { downloadJson, exportFilename } from '../gdpr/gdpr-download.js';
import { BookExportRestoreError, runBookExport } from './take-book.js';

/**
 * WS11 — "Take your book": the clean-exit, per-salon export of the practitioner's OWN sealed book
 * (brief 11; ADR-0012 §portability). The export endpoint (GET /gdpr/book/export) runs under the
 * CURRENT active tenant only, so to export salon X we switch INTO X, run the live export, download it,
 * and switch back — the switch/restore is the try/FINALLY in runBookExport (so a failed export never
 * strands the operator in the wrong salon). Each renter residency the caller holds (renterId != null)
 * gets its own button — a private-practice leg is labelled "Your studio".
 *
 * ONLINE-ONLY: the export is a live fetch, never offline-queued, so every button is disabled offline
 * with an honest note (mirrors the GDPR controls). IMPORT is deliberately NOT here — re-establishing a
 * book at a new salon needs a bulk client-create endpoint (an API change), so we ship export + display
 * only and say so plainly.
 */
export function TakeYourBook() {
  const { user, switchSalon } = useAuth();
  const { online } = useOffline();
  const toast = useToast();
  const exportBook = useExportBook();
  const { data: residencies = [], isLoading } = useResidencies(Boolean(user));

  // Only renter residencies hold a sealed book to take (renterId set). Shared-book memberships can
  // never match by construction (ADR-0012 §3), so they are not offered an export here. Private-
  // practice legs first, then alphabetical — your own studio reads at the top.
  const exportable = residencies
    .filter((r) => r.renterId != null)
    .sort(
      (a, b) =>
        Number(b.isPrivatePractice) - Number(a.isPrivatePractice) ||
        a.salonName.localeCompare(b.salonName),
    );

  // The salon we are mid-switch into for an export (drives the per-row "Switching to …" busy state).
  const [busySalonId, setBusySalonId] = useState<string | null>(null);

  /** Best-effort display name for the home tenant (the residency list carries names); '' if unknown. */
  function homeSalonName(all: Residency[], homeSalonId: string): string {
    return all.find((r) => r.salonId === homeSalonId)?.salonName ?? '';
  }

  async function onTake(residency: Residency) {
    if (!user) return;
    setBusySalonId(residency.salonId);
    try {
      const bundle = await runBookExport({
        currentSalonId: user.salonId,
        targetSalonId: residency.salonId,
        switchSalon,
        exportBook: () => exportBook.mutateAsync(),
        download: (b) =>
          downloadJson(
            exportFilename(`${residency.salonName}-sealed-book-${b.clients.length}-clients`),
            b,
          ),
      });
      toast.success(
        `Exported ${bundle.clients.length} client${bundle.clients.length === 1 ? '' : 's'} from ${residency.salonName}`,
      );
    } catch (err) {
      if (err instanceof BookExportRestoreError) {
        // The export ran, but switching you back failed — you are NOT "back where you started",
        // you're stranded in the target salon. Be honest and point at the salon switcher.
        const homeName = homeSalonName(residencies, user.salonId);
        toast.error(
          err.exportSucceeded
            ? `Exported your book — but we couldn't switch you back${homeName ? ` to ${homeName}` : ''}. Use the salon switcher to return.`
            : `Couldn't export, and couldn't switch you back${homeName ? ` to ${homeName}` : ''}. Use the salon switcher to return.`,
        );
      } else {
        // The common case: the export failed but the switch-back succeeded, so the operator really is
        // back where they started — the original honest message holds.
        toast.error(
          `Could not export your book from ${residency.salonName} — you're back where you started. This is online-only; try again.`,
        );
      }
    } finally {
      setBusySalonId(null);
    }
  }

  return (
    <Card>
      <div className="section-title">Take your book</div>
      <div className="muted" style={{ fontSize: 13, marginTop: 4, lineHeight: 1.5 }}>
        Download a machine-readable copy of your sealed book from any salon where you rent a chair —
        your clients are yours to take with you. We switch you into that salon to export, then
        switch you back automatically.
      </div>

      {isLoading ? (
        <div className="muted" style={{ fontSize: 13, padding: '12px 0 0' }}>
          Loading your books…
        </div>
      ) : exportable.length === 0 ? (
        <div className="muted" style={{ fontSize: 13, padding: '12px 0 0', lineHeight: 1.5 }}>
          You don’t hold a sealed book anywhere yet. When you rent a chair with your own sealed
          book, you’ll be able to export it from here.
        </div>
      ) : (
        <div className="settings-list" style={{ marginTop: 8 }}>
          {exportable.map((r) => {
            const switching = busySalonId === r.salonId;
            return (
              <div key={`${r.salonId}:${r.staffId}`} className="settings-row">
                <div>
                  <div style={{ fontWeight: 600 }}>
                    {r.salonName}
                    {r.isPrivatePractice ? (
                      <Pill tone="cobalt" className="mono">
                        Your studio
                      </Pill>
                    ) : null}
                  </div>
                  <div className="muted" style={{ fontSize: 12.5, marginTop: 2 }}>
                    {switching
                      ? `Switching to ${r.salonName} to export…`
                      : 'Your sealed book in this salon'}
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!online || busySalonId != null}
                  onClick={() => void onTake(r)}
                >
                  {switching ? 'Exporting…' : 'Take your book'}
                </Button>
              </div>
            );
          })}
        </div>
      )}

      {!online ? (
        <div className="muted" style={{ fontSize: 12, marginTop: 10 }}>
          Exporting your book is online-only — reconnect to take a copy.
        </div>
      ) : null}

      <div className="muted" style={{ fontSize: 12, marginTop: 10, lineHeight: 1.5 }}>
        Re-establishing your book at a new salon (importing it back in) is coming — for now you can
        export and keep your own copy.
      </div>
    </Card>
  );
}
